<template>
  <SubscriptionNavigation
    @submit-clicked="onSubmit"
    :is-submitting-data="isSaveSubscriptionInProgress"
    :is-loading-data="isGetSubscriptionInProgress"
  >
    <div data-cy="electricity-supplier-page">
      <div class="text-center">
        <AtomText
          :text="t('subscription.electricitySupplier.title')"
          font-style="font-semibold"
          font-size="text-xl"
          data-cy="installation-address-page-title"
        />
      </div>
      <div>
        <AtomText
          :text="t('subscription.electricitySupplier.contractedPower.title')"
          font-style="font-medium"
          font-size="text-sm"
          class="mt-6"
          data-cy="electricity-supplier-contracted-power-title"
        />
        <OrganismSelect
          v-model="selectedContractedPower"
          :options="contractedPowersChoicesOptions"
          name="contracted-power"
          :placeholder="
            t('subscription.electricitySupplier.contractedPower.form.select.placeholder')
          "
          select-trigger-aria-label="electricity-supplier-select"
          data-cy="electricity-supplier-contracted-power-select"
          :disabled="isGetSubscriptionContractedPowersChoicesInProgress"
          :select-state="getInputState({ errorMessage: contractedPowerError })"
          :helper="getHelperMessage({ errorMessage: contractedPowerError })"
          class="mt-1"
        />
        <MoleculeStandaloneLink
          :label="t('subscription.electricitySupplier.contractedPower.link')"
          data-cy="electricity-supplier-contracted-power-link"
          class="mt-2"
          target="blank"
          type="url"
          font-size="sm"
          icon-name="ExternalLinkIcon"
          to="https://help.mylight150.com/fr/articles/497481-comment-choisir-ma-puissance-souscrite"
          color-variant="brand"
        />
      </div>
      <MoleculeFrameCard
        class="mt-8"
        spacing="sm"
        mainTheme="success"
        data-cy="electricity-supplier-green-offer-card"
      >
        <div class="flex gap-1 items-center">
          <AtomIcon
            class="mx-3"
            iconName="EcoModeIcon"
            colorVariant="success"
            iconType="outline"
            size="lg"
          />
          <div class="flex flex-col gap-1">
            <div>
              <AtomText
                is-tag="span"
                font-style="font-semibold"
                font-size="text-lg"
                :text="tm('subscription.electricitySupplier.greenOffer.title')[0]"
                data-cy="electricity-supplier-green-offer-title"
              />&nbsp;<AtomText
                is-tag="span"
                font-style="font-semibold"
                font-size="text-lg"
                color-variant="success"
                :text="tm('subscription.electricitySupplier.greenOffer.title')[1]"
                data-cy="electricity-supplier-green-offer-title"
              />
            </div>
            <AtomText
              is-tag="div"
              font-size="text-sm"
              class="text-cime-black-light"
              :text="t('subscription.electricitySupplier.greenOffer.description')"
              data-cy="electricity-supplier-green-offer-description"
            />
          </div>
        </div>
        <div
          v-if="isEligibleForWelcomeOffer"
          class="mt-4 rounded-lg border border-cime-gray-light bg-cime-white p-4 text-sm text-cime-gray-dark"
          data-cy="electricity-supplier-welcome-offer"
        >
          <span class="font-bold">
            {{ t('subscription.electricitySupplier.greenOffer.welcomeOffer.label') }}
          </span>
          <span>
            {{ t('subscription.electricitySupplier.greenOffer.welcomeOffer.description') }}
          </span>
        </div>
      </MoleculeFrameCard>
      <div v-if="selectedContractedPower" data-cy="electricity-supplier-pricing-option-section">
        <MoleculeLabel
          :text="t('subscription.electricitySupplier.pricingOption.label')"
          label-for="pricing-options"
          class="mt-4"
          data-cy="electricity-supplier-pricing-option-label"
        />
        <OrganismRadioGroupCard
          v-model="currentPricingOption"
          :cards="pricingOptionsCards"
          orientation="horizontal"
          class="mt-1"
          name="pricing-options"
          :has-error="isSubmitted && !isPricingOptionValid"
          :error-message="t('common.forms.required')"
          data-cy="electricity-supplier-pricing-options-radio-group-cards"
        />
        <MoleculeStandaloneLink
          :label="t('subscription.electricitySupplier.pricingOption.link')"
          data-cy="electricity-supplier-contracted-power-link"
          class="mt-2"
          target="blank"
          type="url"
          font-size="sm"
          icon-name="ExternalLinkIcon"
          to="https://help.mylight150.com/fr/articles/567394-base-ou-heures-pleines-heures-creuses-quel-tarif-choisir"
          color-variant="brand"
        />
        <OrganismAlert
          v-if="!isOffPeakPricingOptionAvailable"
          :title="t('subscription.electricitySupplier.pricingOption.alert')"
          has-icon
          state="info"
          class="mt-4"
          data-cy="electricity-supplier-pricing-option-alert"
        />
        <OrganismAlert
          v-if="currentPricingOption && isEligibleForWelcomeOffer"
          variant="complex"
          state="success"
          has-icon
          :title="t('subscription.electricitySupplier.pricingOption.priceAlert.title')"
          :content-html="priceAlertContentHtml"
          class="mt-4"
          data-cy="electricity-supplier-price-alert"
        />
      </div>
      <template v-if="selectedSubscriptionMonthlyPrice !== null">
        <AtomDivider class="mt-12 mb-12" />
        <MoleculeFrameCard
          data-cy="electricity-supplier-subscription-price"
          spacing="sm"
          content-theme="dark"
        >
          <div class="flex justify-between items-center">
            <AtomText
              :text="t('subscription.electricitySupplier.subscriptionPrice.label')"
              font-size="text-sm"
              font-style="font-medium"
            />
            <div class="flex items-baseline gap-1">
              <AtomText
                is-tag="span"
                font-size="text-xl"
                font-style="font-bold"
                :text="formatEuroPrice(selectedSubscriptionMonthlyPrice)"
                data-cy="electricity-supplier-subscription-price-value"
              />
              <AtomText
                is-tag="span"
                font-size="text-sm"
                color-variant="description"
                :text="t('subscription.electricitySupplier.subscriptionPrice.unit')"
              />
            </div>
          </div>
        </MoleculeFrameCard>
        <OrganismAlert
          :title="t('subscription.electricitySupplier.whyTwoSubscriptions.title')"
          has-icon
          state="info"
          class="mt-4"
          variant="complex"
          :contentText="t('subscription.electricitySupplier.whyTwoSubscriptions.description')"
          :contentList="[
            t('subscription.electricitySupplier.whyTwoSubscriptions.electricitySupply'),
            t('subscription.electricitySupplier.whyTwoSubscriptions.virtualStorage'),
          ]"
          data-cy="electricity-supplier-why-two-subscriptions"
        >
        </OrganismAlert>
      </template>
    </div>
  </SubscriptionNavigation>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { useField } from 'vee-validate';
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import * as yup from 'yup';

import useInputState from '@/composables/useInputState.composable';
import { useElectricitySupplierStore, useStepStore, useSubscriptionStore } from '@/stores';
import useToastSnackbarStore from '@/stores/toastSnackbar.store';

import type {
  SubscriptionContractedPowerAvailable,
  SubscriptionPricingOptionType,
} from '@/types/subscription.type';
import { getErrorToasterConfig } from '@/utils/error.util';
import { formatEuroPrice } from '@/utils/format.util';

import SubscriptionNavigation from '@/components/SubscriptionNavigation.component.vue';

import {
  ESubscriptionPricingOptionType,
  EWelcomeOfferPrices,
} from '@/constants/subscription.constants';
import * as Sentry from '@sentry/vue';

type ContractedPowerSelectOption = {
  id: number;
  value: SubscriptionContractedPowerAvailable;
  label: string;
};

const { t, tm } = useI18n();

const subscriptionStore = useSubscriptionStore();
const stepStore = useStepStore();
const electricitySupplierStore = useElectricitySupplierStore();
const { addToast } = useToastSnackbarStore();
const { goToNextStep } = stepStore;

const { findSubscriptionContractedPowersChoices } = electricitySupplierStore;

const {
  currentSubscription,
  isSaveSubscriptionInProgress,
  isGetSubscriptionInProgress,
  isEligibleForWelcomeOffer,
} = storeToRefs(subscriptionStore);

const { isGetSubscriptionContractedPowersChoicesInProgress } =
  storeToRefs(electricitySupplierStore);

const { getInputState, getHelperMessage } = useInputState();

const isSubmitted = ref(false);

const contractedPowersChoicesOptions = ref<ContractedPowerSelectOption[]>([]);
const currentPricingOption = ref<SubscriptionPricingOptionType | null>(null);

const {
  value: selectedContractedPower,
  errorMessage: contractedPowerError,
  validate: validateContractedPower,
} = useField<ContractedPowerSelectOption | undefined>(
  'contracted-power',
  yup.object({
    id: yup.number(),
    label: yup.string(),
    value: yup.object({
      kVA: yup.number().required(t('common.forms.required')),
    }),
  }),
  {
    validateOnValueUpdate: false,
  }
);

const isOffPeakPricingOptionAvailable = computed(() => selectedContractedPower.value?.id !== 3);

const isBasePricingOptionAvailable = computed(
  () => selectedContractedPower.value?.value.price.base !== null
);

const isPricingOptionValid = computed(() => !!currentPricingOption.value);

const pricingOptionTag = computed(() => ({
  value: t('subscription.electricitySupplier.pricingOption.tag'),
  type: 'success',
}));

const pricingOptionsCards = computed(() => [
  {
    id: ESubscriptionPricingOptionType.Base,
    value: ESubscriptionPricingOptionType.Base,
    title: t('subscription.electricitySupplier.pricingOption.base'),
    description: t('subscription.electricitySupplier.pricingOption.baseDescription'),
    tag: pricingOptionTag.value,
    isDisabled: !isBasePricingOptionAvailable.value,
  },
  {
    id: ESubscriptionPricingOptionType.ExtendedOffPeakAndPeakHours,
    value: ESubscriptionPricingOptionType.ExtendedOffPeakAndPeakHours,
    title: t('subscription.electricitySupplier.pricingOption.offPeakAndPeakHours'),
    description: t('subscription.electricitySupplier.pricingOption.offPeakAndPeakHoursDescription'),
    tag: pricingOptionTag.value,
    isDisabled: !isOffPeakPricingOptionAvailable.value,
  },
]);

const priceAlertContentHtml = computed(() => {
  if (currentPricingOption.value === ESubscriptionPricingOptionType.Base) {
    const kVA = selectedContractedPower.value?.value.kVA ?? 0;
    const basePrices =
      kVA <= 6 ? EWelcomeOfferPrices.Base6KvaOrLess : EWelcomeOfferPrices.BaseMoreThan6Kva;

    return t('subscription.electricitySupplier.pricingOption.priceAlert.base', {
      discountedPrice: basePrices.discounted,
      standardPrice: basePrices.standard,
    });
  }

  return t('subscription.electricitySupplier.pricingOption.priceAlert.offPeakAndPeakHours', {
    offPeakDiscountedPrice: EWelcomeOfferPrices.OffPeakHours.discounted,
    offPeakStandardPrice: EWelcomeOfferPrices.OffPeakHours.standard,
    peakDiscountedPrice: EWelcomeOfferPrices.PeakHours.discounted,
    peakStandardPrice: EWelcomeOfferPrices.PeakHours.standard,
  });
});

const selectedSubscriptionMonthlyPrice = computed<number | null>(() => {
  if (
    !selectedContractedPower.value ||
    !selectedContractedPower.value.value.price ||
    !currentPricingOption.value
  ) {
    return null;
  }
  const price = selectedContractedPower.value.value.price;
  if (currentPricingOption.value === ESubscriptionPricingOptionType.Base) {
    return price.base;
  }
  if (currentPricingOption.value === ESubscriptionPricingOptionType.ExtendedOffPeakAndPeakHours) {
    return price.offPeakAndPeakHours;
  }
  return null;
});

const getContractedPowerChoices = async () => {
  try {
    const contractedPowersChoices = await findSubscriptionContractedPowersChoices();
    contractedPowersChoicesOptions.value = contractedPowersChoices.availableContractedPowers.map(
      (contractedPower: SubscriptionContractedPowerAvailable) => ({
        id: contractedPower.kVA,
        value: contractedPower,
        label: t('subscription.electricitySupplier.contractedPower.form.select.optionsLabel', {
          size: contractedPower.kVA,
        }),
      })
    );
  } catch (error) {
    Sentry.captureException(error, {
      tags: { feature: 'electricity-supplier', action: 'findSubscriptionContractedPowersChoices' },
    });
    const toastConfig = getErrorToasterConfig({
      error,
      toasterId: 'electricity-supplier',
      defaultMessage: t(
        'subscription.electricitySupplier.api.errors.getSubscriptionContractedPowersChoices'
      ),
    });
    addToast(toastConfig);
  }
};

const selectDefaultContractedPowerChoice = () => {
  if (currentSubscription.value.energyOfferInformations?.contractedPower) {
    selectedContractedPower.value = contractedPowersChoicesOptions.value.find(
      option =>
        option.value.kVA === currentSubscription.value.energyOfferInformations?.contractedPower.kVA
    );
  }
};

const selectDefaultPricingOption = () => {
  const subscriptionPricingOption =
    currentSubscription.value.energyOfferInformations?.energyPricingOption;

  if (
    subscriptionPricingOption === ESubscriptionPricingOptionType.ExtendedOffPeakAndPeakHours ||
    subscriptionPricingOption === ESubscriptionPricingOptionType.OffPeakAndPeakHours
  ) {
    currentPricingOption.value = ESubscriptionPricingOptionType.ExtendedOffPeakAndPeakHours;
    return;
  }

  if (subscriptionPricingOption === ESubscriptionPricingOptionType.Base) {
    currentPricingOption.value = ESubscriptionPricingOptionType.Base;
    return;
  }
  currentPricingOption.value = null;
};

onMounted(async () => {
  await getContractedPowerChoices();
  selectDefaultContractedPowerChoice();
  selectDefaultPricingOption();
});

const onSubmit = async () => {
  isSubmitted.value = true;
  const { valid: isContractedPowerValid } = await validateContractedPower();

  if (isContractedPowerValid && isPricingOptionValid.value) {
    try {
      const { kVA } = selectedContractedPower.value!.value;
      await subscriptionStore.updateSubcriptionWithEnergyOfferInformations({
        contractedPower: { kVA },
        energyPricingOption: currentPricingOption.value,
      });

      await goToNextStep();
    } catch (error) {
      const toastConfig = getErrorToasterConfig({
        error,
        toasterId: 'electricity-supplier',
        defaultMessage: t(
          'subscription.electricitySupplier.api.errors.patchSubscriptionEnergyOfferInformations'
        ),
      });
      addToast(toastConfig);
    }
  }
};

watch(
  () => isOffPeakPricingOptionAvailable.value,
  newValue => {
    if (!newValue) {
      currentPricingOption.value = ESubscriptionPricingOptionType.Base;
    }
  }
);

watch(
  () => isBasePricingOptionAvailable.value,
  newValue => {
    if (!newValue) {
      currentPricingOption.value = ESubscriptionPricingOptionType.ExtendedOffPeakAndPeakHours;
    }
  }
);
</script>

<style scoped></style>
